import { router, useForm, usePage } from "@inertiajs/react"
import { Copy, Download, QrCode, Link2, Wifi, Mail, Plus, Check } from "lucide-react"
import React, { type ReactNode, useState } from "react"
import { toast } from "sonner"

import { AdminLayout } from "@/layouts/AdminLayout"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { GuideOption, GuideShareCard, PropertySummary, ShareType } from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  property: PropertySummary
  shares: GuideShareCard[]
  guides: GuideOption[]
  urls: { toggle_share: string; create_share: string; download_qr_base: string }
}

const SHARE_TYPES = ["qr", "link", "wifi_redirect", "pdf_letter"] as const
type ShareTypeKey = (typeof SHARE_TYPES)[number]

const CARD_CONFIG: Record<ShareType, { title: string; icon: React.ComponentType<{ className?: string }>; description: string }> = {
  qr:           { title: "QR Code",       icon: QrCode, description: "Print or display this QR for guests to scan" },
  link:         { title: "Direct Link",   icon: Link2,  description: "Share this URL via WhatsApp, email, or SMS" },
  wifi_redirect:{ title: "WiFi Redirect", icon: Wifi,   description: "Configure your captive portal to redirect here" },
  pdf_letter:   { title: "Welcome Letter",icon: Mail,   description: "Personalised PDF for each guest" },
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  )
}

function ShareCard({
  share,
  toggleShareUrl,
  downloadQrBaseUrl,
}: {
  share: GuideShareCard
  toggleShareUrl: string
  downloadQrBaseUrl: string
}) {
  const config = CARD_CONFIG[share.share_type]
  const Icon = config.icon
  const [toggling, setToggling] = useState(false)

  function handleToggle() {
    setToggling(true)
    router.patch(
      toggleShareUrl,
      { share_id: share.id },
      {
        preserveScroll: true,
        onFinish: () => setToggling(false),
      }
    )
  }

  const toggleButton = share.active ? (
    // Issue 1: Deactivate requires confirmation — accidental click could break live WiFi/QR
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto border-destructive text-destructive hover:bg-destructive/10"
          disabled={toggling}
        >
          Deactivate
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {config.title}?</AlertDialogTitle>
          <AlertDialogDescription>
            Guests who scan or follow this {config.title.toLowerCase()} will no longer reach your guide.
            {share.share_type === "wifi_redirect" && " This will affect all guests connecting via WiFi."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleToggle}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Deactivate
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ) : (
    // Issue 2: Activate uses outline variant for visibility, green tint to signal positive action
    <Button
      variant="outline"
      size="sm"
      className="ml-auto border-green-600 text-green-700 hover:bg-green-50"
      onClick={handleToggle}
      disabled={toggling}
    >
      {toggling ? "Activating…" : "Activate"}
    </Button>
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">{config.title}</CardTitle>
              {share.label && (
                <p className="text-xs text-muted-foreground">{share.label}</p>
              )}
            </div>
          </div>
          <Badge variant={share.active ? "default" : "secondary"}>
            {share.active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{share.guide_title}</p>
      </CardHeader>

      <CardContent className="space-y-3 pb-2">
        {share.share_type === "qr" && share.qr_svg && (
          /* SVG generated server-side by QrCodeGenerator — trusted content, not user input */
          <div
            className="flex justify-center rounded-lg border bg-white p-3"
            dangerouslySetInnerHTML={{ __html: share.qr_svg }}
          />
        )}

        {/* Issue 4: title attribute shows full URL on hover for truncated display */}
        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
          <span
            className="flex-1 truncate font-mono text-xs text-muted-foreground"
            title={share.url}
          >
            {share.url}
          </span>
          <CopyButton text={share.url} />
        </div>

        {share.share_type === "wifi_redirect" && (
          <p className="text-xs text-muted-foreground">
            Enter this URL as the redirect destination in your captive portal configuration.
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {share.access_count}{" "}
            {share.share_type === "qr" ? "scans" : share.share_type === "wifi_redirect" ? "redirects" : "visits"}
          </span>
          {share.last_accessed_at && (
            <span>Last: {new Date(share.last_accessed_at).toLocaleDateString()}</span>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2 pt-2">
        {/* Issue 3: Plain "SVG"/"PNG" is meaningless to non-technical users — use descriptive labels */}
        {share.share_type === "qr" && (
          <>
            <Button variant="outline" size="sm" className="gap-1.5" asChild>
              <a
                href={`${downloadQrBaseUrl}?share_id=${share.id}&fmt=svg`}
                download
                aria-label="Download QR code for print (SVG)"
                title="Best for printing — scales to any size without losing quality"
              >
                <Download className="h-3.5 w-3.5" /> For print
              </a>
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" asChild>
              <a
                href={`${downloadQrBaseUrl}?share_id=${share.id}&fmt=png`}
                download
                aria-label="Download QR code for screens (PNG)"
                title="Best for websites, emails, and digital displays"
              >
                <Download className="h-3.5 w-3.5" /> For screens
              </a>
            </Button>
          </>
        )}
        {toggleButton}
      </CardFooter>
    </Card>
  )
}

function EmptyCard({ type, onCreateClick }: { type: ShareTypeKey; onCreateClick: () => void }) {
  const config = CARD_CONFIG[type]
  const Icon = config.icon

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base text-muted-foreground">{config.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-sm text-muted-foreground">{config.description}</p>
      </CardContent>
      <CardFooter className="pt-2">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={onCreateClick}>
          <Plus className="h-3.5 w-3.5" /> Create
        </Button>
      </CardFooter>
    </Card>
  )
}

function ComingSoonCard({ type }: { type: ShareTypeKey }) {
  const config = CARD_CONFIG[type]
  const Icon = config.icon

  return (
    <Card className="border-dashed opacity-60">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base text-muted-foreground">{config.title}</CardTitle>
          </div>
          <Badge variant="outline">Coming soon</Badge>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <p className="text-sm text-muted-foreground">{config.description}</p>
      </CardContent>
    </Card>
  )
}

function CreateShareDialog({
  createShareUrl,
  guides,
  open,
  onOpenChange,
  defaultShareType,
}: {
  createShareUrl: string
  guides: GuideOption[]
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultShareType?: string
}) {
  const { data, setData, post, processing, errors, reset } = useForm({
    guide_id:   "",
    share_type: defaultShareType ?? "",
    label:      "",
  })
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createShareUrl, {
      onSuccess: () => { reset(); onOpenChange(false) },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Share</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="guide_id">Guide</Label>
            <Select value={data.guide_id} onValueChange={(v) => setData("guide_id", v)}>
              <SelectTrigger id="guide_id">
                <SelectValue placeholder="Select a guide…" />
              </SelectTrigger>
              <SelectContent>
                {/* Issue 5: Show empty state when no active guides exist */}
                {guides.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    No active guides found. Activate a guide first.
                  </div>
                ) : (
                  guides.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>{g.title}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {errors.guide_id && <p className="text-xs text-destructive">{errors.guide_id}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="share_type">Type</Label>
            <Select value={data.share_type} onValueChange={(v) => setData("share_type", v)}>
              <SelectTrigger id="share_type">
                <SelectValue placeholder="Select type…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qr">QR Code — guests scan to open the guide</SelectItem>
                <SelectItem value="link">Direct Link — share via WhatsApp, email, or SMS</SelectItem>
                <SelectItem value="wifi_redirect">WiFi Redirect — captive portal destination</SelectItem>
              </SelectContent>
            </Select>
            {errors.share_type && <p className="text-xs text-destructive">{errors.share_type}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="label">Label <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="label"
              value={data.label}
              onChange={(e) => setData("label", e.target.value)}
              placeholder="e.g. Lobby QR, Front desk…"
            />
            {errors.label && <p className="text-xs text-destructive">{errors.label}</p>}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing || !data.guide_id || !data.share_type || guides.length === 0}
            >
              {processing ? "Creating…" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function DeliveryIndex({ property, shares, guides, urls }: Props) {
  const { config } = usePage<SharedProps>().props
  const [createOpen, setCreateOpen] = useState(false)
  const [defaultShareType, setDefaultShareType] = useState<string | undefined>()

  function openCreate(shareType?: string) {
    setDefaultShareType(shareType)
    setCreateOpen(true)
  }

  const sharesByType = (type: ShareTypeKey) =>
    shares.filter((s) => s.share_type === type)

  const wifiRedirectUrl = `${config.guest_base_url}/${property.slug}`

  function handleCopyWifiUrl() {
    navigator.clipboard.writeText(wifiRedirectUrl)
      .then(() => toast("Copied to clipboard"))
      .catch(() => toast("Failed to copy — please copy manually"))
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Delivery</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openCreate()}>
          <Plus className="h-4 w-4" /> Add Share
        </Button>
      </div>

      <div className="mb-6 space-y-1.5">
        <Label>WiFi redirect URL</Label>
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={wifiRedirectUrl}
            className="font-mono text-sm"
            data-testid="wifi-redirect-url-input"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyWifiUrl}
                  aria-label="Copy WiFi redirect URL"
                  data-testid="copy-wifi-url-button"
                >
                  <Copy className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Paste this URL into your WiFi controller's redirect target URL field
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2" data-testid="delivery-grid">
        {SHARE_TYPES.map((type) => {
          if (type === "pdf_letter") return <ComingSoonCard key={type} type={type} />
          const typeShares = sharesByType(type)
          if (typeShares.length > 0)
            return (
              <React.Fragment key={type}>
                {typeShares.map((share) => (
                  <ShareCard
                    key={share.id}
                    share={share}
                    toggleShareUrl={urls.toggle_share}
                    downloadQrBaseUrl={urls.download_qr_base}
                  />
                ))}
              </React.Fragment>
            )
          return <EmptyCard key={type} type={type} onCreateClick={() => openCreate(type)} />
        })}
      </div>

      <CreateShareDialog
        key={defaultShareType ?? "default"}
        createShareUrl={urls.create_share}
        guides={guides}
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultShareType={defaultShareType}
      />
    </div>
  )
}

DeliveryIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
